import React from 'react';
import { Input } from '@roger/ui-components';
import type {
  DemoClinicIdentity,
  PulledClinic,
} from '../../services/api/types.gen';

/**
 * One pulled clinic's identity fields, typed here rather than in Admin,
 * because every build deletes the clinic rows an Admin edit would live on.
 *
 * A blank field is not a value: the build uses Kinnser's, shown as the
 * placeholder, or the demo value when Kinnser has none. So only what is typed
 * is stored, and clearing a field hands it back to Kinnser.
 *
 * Labels match Agency Data Fill's identity grid, so one number has one name on
 * both screens.
 */

type Field = keyof DemoClinicIdentity;

const FIELDS: { key: Field; label: string; pulled?: keyof PulledClinic }[] = [
  { key: 'npi', label: 'NPI', pulled: 'npi' },
  { key: 'ein', label: 'EIN' },
  { key: 'hha_agency_id', label: 'CCN / PTAN', pulled: 'hha_agency_id' },
  { key: 'address', label: 'Street address', pulled: 'address' },
  { key: 'city', label: 'City', pulled: 'city' },
  { key: 'state', label: 'State', pulled: 'state' },
  { key: 'zip_code', label: 'ZIP', pulled: 'zip_code' },
];

/** The entries with this one field changed, dropping whatever ends up empty. */
function withIdentityField(
  identities: Record<string, DemoClinicIdentity>,
  clinicId: string,
  field: Field,
  value: string
): Record<string, DemoClinicIdentity> {
  const entry: DemoClinicIdentity = { ...identities[clinicId] };
  if (value.trim()) entry[field] = value;
  else delete entry[field];
  const next = { ...identities };
  if (Object.keys(entry).length) next[clinicId] = entry;
  else delete next[clinicId];
  return next;
}

export const ClinicIdentityFields: React.FC<{
  clinic: PulledClinic;
  identities: Record<string, DemoClinicIdentity>;
  onChange: (identities: Record<string, DemoClinicIdentity>) => void;
}> = ({ clinic, identities, onChange }) => (
  /* One across on a phone, up to four on a wide pane. */
  <div className='grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4'>
    {FIELDS.map(field => {
      const pulled = field.pulled ? clinic[field.pulled] : null;
      return (
        <label key={field.key} className='space-y-1'>
          <span className='text-xs text-muted-foreground'>{field.label}</span>
          <Input
            inputSize='sm'
            aria-label={`${clinic.name} ${field.label}`}
            value={identities[clinic.clinic_id]?.[field.key] ?? ''}
            placeholder={
              typeof pulled === 'string' && pulled
                ? pulled
                : field.key === 'ein'
                  ? 'Ask the agency for this'
                  : 'Demo value'
            }
            onChange={event =>
              onChange(
                withIdentityField(
                  identities,
                  clinic.clinic_id,
                  field.key,
                  event.target.value
                )
              )
            }
          />
        </label>
      );
    })}
  </div>
);

export default ClinicIdentityFields;
